import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sidebar } from "./Sidebar";
import { Topbar } from "./Topbar";
import { ShellProvider, useShell } from "./shellContext";

// Map the active route to its topbar title key.
const TITLE_BY_PATH: Record<string, string> = {
  "/overview": "nav.overview",
  "/metrics": "nav.metrics",
  "/renewals": "nav.renewals",
  "/customers": "nav.customers",
  "/settings": "nav.settings",
  "/import": "nav.import",
  "/notifications": "nav.notifications",
  "/catalogue": "nav.catalogue",
};

function ShellInner() {
  const { t } = useTranslation();
  const location = useLocation();
  const { customerCount } = useShell();
  const [navOpen, setNavOpen] = useState(false);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const closeNav = useCallback(() => setNavOpen(false), []);

  const titleKey = TITLE_BY_PATH[location.pathname] ?? "nav.overview";
  const pageTitle = t(titleKey);

  // Reflect the active page in the browser/Chrome tab title ("<page> · Nasiyah").
  // Re-runs on route change and on language switch (pageTitle is the resolved string).
  useEffect(() => {
    document.title = `${pageTitle} · ${t("app.name")}`;
  }, [pageTitle, t]);

  // A route change always lands with the mobile nav closed.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Column widths live in index.css (.app-shell): 248px → 76px rail (<=1100px) → none (<768px).
  // h-[100dvh], not h-screen: iOS Safari's collapsing URL bar would otherwise hide the bottom.
  return (
    <div className="app-shell grid h-[100dvh] overflow-hidden">
      <Sidebar customerCount={customerCount} />
      <main className="flex min-h-0 min-w-0 flex-col">
        <Topbar
          ref={menuBtnRef}
          title={pageTitle}
          onMenu={() => setNavOpen(true)}
          menuOpen={navOpen}
        />
        <div className="flex-1 overflow-y-auto overscroll-contain px-4 pb-10 pt-5 md:p-7">
          <div className="page-enter">
            <Outlet />
          </div>
        </div>
      </main>
      <MobileNav
        open={navOpen}
        onClose={closeNav}
        restoreRef={menuBtnRef}
        label={t("topbar.menu")}
        customerCount={customerCount}
      />
    </div>
  );
}

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  restoreRef: React.RefObject<HTMLButtonElement>;
  label: string;
  customerCount?: number;
}

// Off-canvas nav for <768px: the full Sidebar in a left sheet over a scrim.
// Always mounted so the slide runs both ways; `inert` while parked keeps its links
// out of the tab order. Escape / scrim / nav tap close it; focus returns to the menu button.
function MobileNav({ open, onClose, restoreRef, label, customerCount }: MobileNavProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.toggleAttribute("inert", !open);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const restore = restoreRef.current;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    panelRef.current?.querySelector<HTMLElement>("a[aria-current=page], a")?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      restore?.focus();
    };
  }, [open, onClose, restoreRef]);

  return (
    <div className="md:hidden">
      <div className={`scrim ${open ? "show" : ""}`} onClick={onClose} aria-hidden />
      <div
        id="mobile-nav"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={`nav-sheet ${open ? "show" : ""}`}
      >
        <Sidebar variant="sheet" customerCount={customerCount} onClose={onClose} />
      </div>
    </div>
  );
}

export function AppShell() {
  return (
    <ShellProvider>
      <ShellInner />
    </ShellProvider>
  );
}
